import { computed, action, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IImpersonationConfiguration } from '../models';
import { ISearchProps } from '@kurtosys/ksys-app-components/dist/components/base/Search/models';
import { IRequestOptions } from '@kurtosys/ksys-api-client/dist/models/IRequestOptions';
import { TImpersonationSearchRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TImpersonationSearchRequestBody';
import { TUserName } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TUserName';
import { TImpersonateRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TImpersonateRequestBody';
import { IPagingProps } from '@kurtosys/ksys-app-components/dist/components/base/Paging/models';
import { utils } from '@kurtosys/ksys-app-template';

export class ImpersonationStore extends StoreBase {
	static componentKey: 'impersonation' = 'impersonation';
	@observable initiated = false;
	@observable userList?: TUserName[] = [];
	@observable totalRows = 0;
	@observable currentIndex = 0;
	@observable searchTerm = '';

	@computed
	get configuration(): IImpersonationConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(ImpersonationStore.componentKey);
		}
	}

	@computed
	get impersonationActive(): boolean {
		const { user } = this.storeContext.appStore;
		return user && !utils.typeChecks.isNullOrEmpty((user as any).impersonatorUsername) || false;
	}

	@computed
	get hideActiveImpersonation(): boolean {
		return this.configuration && this.configuration.activeSessionOptions && this.configuration.activeSessionOptions.hideImpersonation || false;
	}

	@computed
	get stopImpersonatingButtonText(): string | undefined {
		const text = this.configuration && this.configuration.activeSessionOptions && this.configuration.activeSessionOptions.stopImpersonatingButtonText ||
			'Stop Impersonating {name}';
		if (this.impersonationActive) {
			return utils.replacePlaceholders(this.storeContext.appStore.user || {}, text);
		}
	}

	@computed
	get impersonationUserListLimit(): number {
		return this.pagingProps && this.pagingProps.pageSize || 5;
	}

	@computed
	get impersonateUserButtonText(): string {
		return (this.configuration && this.configuration.impersonateUserButtonText) ||
			'Impersonate';
	}

	@computed
	get impersonationListHeading(): string | undefined {
		return this.configuration && this.configuration.impersonationListHeading;
	}

	@computed
	get impersonationSearchHeading(): string | undefined {
		return this.configuration && this.configuration.impersonationSearchHeading;
	}

	@computed
	get configuredRedirectUrl(): string | undefined {
		const redirectUrl = this.configuration && this.getQueryValue(this.configuration.redirectUrl);
		return redirectUrl;
	}

	@action
	async initialize(): Promise<void> {
		this.init();
	}

	@action
	sleep = async (interval: number) => {
		return new Promise((resolve, reject) => {
			setTimeout(resolve, interval);
		});
	}

	searchItems = async (term: string, isPagingEvent: boolean = false) => {
		this.searchTerm = term;
		if (!isPagingEvent) {
			this.currentIndex = 0;
		}
		await this.impersonationSearch();

		//Below is a work around for existing Search App component
		await this.sleep(200);
		const response = {
			term,
			records: [],
		};
		return response;
	}

	init = async () => {
		await this.impersonationSearch();
		this.initiated = true;
	}

	@action
	stopImpersonating = async () => {
		const { kurtosysApiStore } = this.storeContext;
		try {
			await kurtosysApiStore.stopImpersonating.execute();
			if (this.configuredRedirectUrl) {
				window.location.href = this.configuredRedirectUrl;
			}
			else {
				this.storeContext.appStore.refreshUser();
			}
		}
		catch (ex) {
			console.warn(ex);
		}
	}

	@action
	onClickUser = async (username: string) => {
		try {
			const { kurtosysApiStore, messageStore } = this.storeContext;
			const { componentKey } = ImpersonationStore;

			const overrides: Partial<IRequestOptions<TImpersonateRequestBody>> = {
				body: {
					userName: username,
				},
			};
			const response = await kurtosysApiStore.impersonate.callApi(overrides);
			const responseData = await response.json();
			if (response.status !== 200) {
				messageStore.setErrorText(responseData.error, componentKey);
			}
			else {
				if (this.configuredRedirectUrl) {
					window.location.href = this.configuredRedirectUrl;
				}
				else {
					this.storeContext.appStore.refreshUser();
				}
			}
		}
		catch (e) {
			console.error(e);
			console.log('Unable to impersonate this user');
		}
	}

	@action
	impersonationSearch = async () => {
		const { kurtosysApiStore } = this.storeContext;

		const overrides: Partial<IRequestOptions<TImpersonationSearchRequestBody>> = {
			body: {
				userName: this.searchTerm,
				limit: this.impersonationUserListLimit,
				start: this.currentIndex,
			},
		};

		const response = await kurtosysApiStore.impersonationSearch.execute(overrides);
		this.userList = response.values;
		this.totalRows = response.total;
	}

	@action
	pageBackwards = (currentIndex: number) => {
		this.currentIndex = currentIndex;
		this.searchItems(this.searchTerm, true);
	}

	@action
	pageForwards = (currentIndex: number) => {
		this.currentIndex = currentIndex;
		this.searchItems(this.searchTerm, true);
	}

	@computed
	get pagingProps(): IPagingProps {
		const pagingProps = this.configuration && this.configuration.pagingProps;
		const defaultPagingProps: IPagingProps = {
			totalRows: this.totalRows,
			pageSize: 5,
			pageBackward: this.pageBackwards,
			pageForward: this.pageForwards,
			pagingLabelFormat: '{firstItem} - {lastItem} / {totalRows}',
		};
		return { ...defaultPagingProps, ...pagingProps };
	}

	@computed
	get searchProps(): ISearchProps {
		const searchProps = this.configuration && this.configuration.searchProps;
		const defaultSearchProps: ISearchProps = {
			placeholder: 'Filter users',
			iconPosition: 'left',
			minCharacterTrigger: 1,
			onChangeMillisecondTimeout: 100,
			searchCallback: this.searchItems,
		};
		return { ...defaultSearchProps, ...searchProps };
	}
}